import { useState, useEffect, useCallback, useMemo } from "react";
import styles from "./TargetingRules.module.css";
import { useFeatureFlipStore } from "Store/featureFlipStore";
import { FiAlertCircle, FiSave } from "react-icons/fi";
import { useEditTreatment } from "API/Queries/useEditTreatments";
import { useParams } from "react-router-dom";
import { getNextAvailableColor } from "Utils/utils";
import { useUpdateFeatureDetails } from "API/Queries/useUpdateFeatureDetails";

interface PercentageChange {
  [key: string]: number;
}

const TargetingRules = () => {
  const { isFeatureDynamic, setIsFeatureDynamic, isFeatureEnabled, existingTreatments, colors } =
    useFeatureFlipStore();

  const { id, featureId } = useParams();
  const [defaultTreatment, setDefaultTreatment] = useState("");
  const [percentageChange, setPercentageChange] = useState<PercentageChange>(
    {}
  );
  const [percentageError, setPercentageError] = useState("");
  const [totalPercentage, setTotalPercentage] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialState, setInitialState] = useState({
    isDynamic: false,
    defaultTreatment: "",
    percentages: {} as PercentageChange,
  });

  const { mutateAsync: editTreatment } = useEditTreatment();
  const { mutateAsync: updateFeatureDetails } = useUpdateFeatureDetails();

  // Initialize state from store
  useEffect(() => {
    if (existingTreatments.length > 0) {
      const initialPercentages: PercentageChange = {};
      existingTreatments.forEach((treatment) => {
        if (treatment.rolloutPercentage !== undefined) {
          initialPercentages[treatment.id] = treatment.rolloutPercentage;
        }
      });

      setPercentageChange(initialPercentages);
      setInitialState({
        ...initialState,
        isDynamic: isFeatureDynamic,
        percentages: initialPercentages,
      });
    }
  }, [existingTreatments]);

  // Check for changes
  useEffect(() => {
    const hasDynamicChanged = isFeatureDynamic !== initialState.isDynamic;
    const hasDefaultTreatmentChanged =
      defaultTreatment !== initialState.defaultTreatment;

    // Check if percentages have changed
    const hasPercentagesChanged = Object.keys(percentageChange).some(
      (id) => percentageChange[id] !== initialState.percentages[id]
    );

    setHasChanges(
      hasDynamicChanged || hasDefaultTreatmentChanged || hasPercentagesChanged
    );
  }, [isFeatureDynamic, defaultTreatment, percentageChange, initialState]);

  useEffect(() => {
    const defaultEnabled = existingTreatments.find(
      (treatment) => treatment.default
    );
    setDefaultTreatment(defaultEnabled?.name || "");
    setInitialState((prev) => {
      return { ...prev, defaultTreatment: defaultEnabled?.name || "" };
    });
  }, [existingTreatments]);

  // Calculate total percentage whenever percentageChange changes
  useEffect(() => {
    const total = Object.values(percentageChange).reduce(
      (sum: number, value: number) => sum + value,
      0
    );
    setTotalPercentage(total);

    if (total > 100) {
      setPercentageError(`Total percentage exceeds 100% (currently ${total}%)`);
    } else if (total < 100) {
      setPercentageError(
        `Total percentage is less than 100% (currently ${total}%)`
      );
    } else {
      setPercentageError("");
    }
  }, [percentageChange]);

  const handlePercentageChange = (id: string, value: string) => {
    const numValue = parseInt(value, 10);
    if (isNaN(numValue)) {
      setPercentageChange({ ...percentageChange, [id]: 0 });
    } else {
      setPercentageChange({ ...percentageChange, [id]: numValue });
    }
  };

  // Function to evenly distribute percentages among treatments
  const distributePercentagesEvenly = () => {
    if (existingTreatments.length === 0) return;

    const count = existingTreatments.length;
    const baseValue = Math.floor(100 / count);
    const remainder = 100 % count;

    const newPercentages: PercentageChange = {};

    existingTreatments.forEach((treatment, index) => {
      // Add extra 1% to initial treatments if there's a remainder
      newPercentages[treatment.id] = baseValue + (index < remainder ? 1 : 0);
    });

    setPercentageChange(newPercentages);
  };

  // Handle switching to dynamic mode
  const handleDynamicModeChange = (isDynamic: boolean) => {
    setIsFeatureDynamic(isDynamic);

    // If switching to dynamic mode, distribute percentages evenly
    if (isDynamic) {
      distributePercentagesEvenly();
    }
  };

  const handleCancel = () => {
    setIsFeatureDynamic(initialState.isDynamic);
    setPercentageChange(initialState.percentages);
    setDefaultTreatment(initialState.defaultTreatment);
    setHasChanges(false);
  };

  const handleSave = async () => {
    await updateFeatureDetails({
      projectId: id as string,
      featureId: featureId as string,
      enabled: isFeatureEnabled,
      dynamic: isFeatureDynamic,
    });

    const editedTreatmentsData = existingTreatments.map((treatment) => ({
      ...treatment,
      rolloutPercentage: percentageChange[treatment.id],
      default: defaultTreatment === treatment.name,
    }));

    const editPromises = editedTreatmentsData?.map((treatment) => {
      const payload = {
        projectId: id as string,
        treatmentId: treatment?.id || "",
        ...treatment,
      };
      return editTreatment(payload);
    });

    await Promise.all(editPromises);
    setHasChanges(false);
  };

  return (
    <div className={styles.card}>
      <div className={styles.cardHeader}>
        <h3>Targeting rules</h3>
        {hasChanges && (
          <div className={styles.headerActions}>
            <button className={styles.cancelButton} onClick={handleCancel}>
              Cancel
            </button>
            <button
              className={styles.saveButton}
              onClick={handleSave}
              disabled={isFeatureDynamic && !!percentageError}
            >
              <FiSave />
              Save Changes
            </button>
          </div>
        )}
      </div>
      <div className={styles.defaultRule}>
        <div className={styles.ruleColumns}>
          <div className={styles.ruleColumn}>
            <div className={styles.ruleOption}>
              <input
                type="radio"
                id="serve"
                name="rule"
                checked={!isFeatureDynamic}
                onChange={() => handleDynamicModeChange(false)}
                className={styles.radio}
              />
              <label htmlFor="serve">
                <h4 className={styles.ruleHeading}>Serve single treatment</h4>
                <div className={styles.ruleContent}>
                  <select
                    onChange={(e) => setDefaultTreatment(e.target.value)}
                    className={styles.select}
                    disabled={isFeatureDynamic}
                    value={defaultTreatment}
                  >
                    {existingTreatments.map((treatment) => (
                      <option key={treatment.name} value={treatment.name}>
                        {treatment.name}
                      </option>
                    ))}
                  </select>
                </div>
              </label>
            </div>
          </div>

          <div className={styles.ruleColumn}>
            <div className={styles.ruleOption}>
              <input
                type="radio"
                id="distribute"
                name="rule"
                checked={isFeatureDynamic}
                onChange={() => handleDynamicModeChange(true)}
                className={styles.radio}
              />
              <label htmlFor="distribute">
                <h4 className={styles.ruleHeading}>Distribute treatments</h4>
                <div className={styles.ruleContent}>
                  <div className={styles.distributions}>
                    {existingTreatments.map((treatment, i) => (
                      <div key={treatment.name} className={styles.distribution}>
                        <div
                          className={styles.treatmentColor}
                          style={{
                            background:
                              treatment.name.toLowerCase() === "on"
                                ? "#4945FF"
                                : treatment.name.toLowerCase() === "off"
                                ? "#F87171"
                                : colors[i],
                          }}
                        />
                        <span>{treatment.name}</span>
                        <input
                          type="number"
                          min="0"
                          max="100"
                          value={percentageChange[treatment.id] ?? 0}
                          onFocus={(e) => e.target.select()}
                          onChange={(e) =>
                            handlePercentageChange(treatment.id, e.target.value)
                          }
                          className={styles.percentageInput}
                          disabled={!isFeatureDynamic}
                        />
                        <span className={styles.percentSymbol}>%</span>
                      </div>
                    ))}
                  </div>
                  {isFeatureDynamic && percentageError && (
                    <div className={styles.percentageError}>
                      <FiAlertCircle />
                      {percentageError}
                    </div>
                  )}
                </div>
              </label>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetingRules;
